import { Fragment, useEffect, useRef } from "react";
import { ParsedPointId, ProcessPictureElement } from "@fidelix/fx-margaret";
import { useParsedSearchParams } from "@fidelix/fx-miranda";
import { t } from "@lingui/core/macro";
import { Point } from "../point/point.model";
import { useGraphicsContext } from "./graphics-context";
import { LoadingView } from "../common/loading-view";
import NoOverlay from "./no-overlay";
import { CustomPopover, SetValuePopover } from "./set-value-popover";
import { PopoverHeader } from "./edit-point-popover";
import { EditScadaLinkPopover } from "./edit-link-popover";
import { useRequiredParams } from "../utils/url";
import { useRealtimePointUpdatesContext } from "../contexts/realtime-point-values-context";
import { convertToMargaretPoint } from "./graphics.utils";

// Separate component for socket management to prevent re-renders
function ProcessPictureRenderer() {
  const {
    graphics,
    element,
    setElement,
    attachment,
    attachments,
    pointsQuery,
  } = useGraphicsContext();
  const { subscribeToPoints, updates, closeConnection } =
    useRealtimePointUpdatesContext();
  const { companyId } = useRequiredParams({ companyId: "number" });
  const { parsedParams } = useParsedSearchParams({
    replay: { parse: (p) => p.get("replay") === "true" },
  });

  useEffect(() => {
    if (parsedParams.replay) {
      closeConnection();
      return;
    }

    if (pointsQuery.data) {
      subscribeToPoints(pointsQuery.data.points);
    }
  }, [
    closeConnection,
    companyId,
    parsedParams.replay,
    pointsQuery.data,
    subscribeToPoints,
  ]);

  useEffect(() => {
    if (!element || updates.length === 0) {
      return;
    }

    const pointValues = convertToMargaretPoint(updates);
    element.setValue("values", pointValues);
  }, [element, updates]);

  return (
    <process-picture
      ref={(el: ProcessPictureElement | null) => setElement(el || undefined)}
      markup={attachment!.publicUrl}
      deviceid={graphics!.device?.externalId ?? ""}
      urlmap={JSON.stringify(attachments)}
      nodatatext={t`No data`}
    />
  );
}

export function ProcessPicture() {
  const {
    triggerRect,
    attachment,
    focusedPoint,
    setFocusedPoint,
    sustainFocus,
    isGraphicsLoading,
    graphics,
  } = useGraphicsContext();
  const { companyId } = useRequiredParams({
    companyId: "number",
  });
  // Virtual anchor element used for popover positioning.
  // Unlike elements inside <process-picture>, this stays mounted across re-renders,
  // so the popover keeps a stable reference even after DOM updates.
  const anchorRef = useRef<HTMLDivElement | null>(null);

  if (isGraphicsLoading) {
    return <LoadingView />;
  }

  const Wrapper = ({ children }) => {
    return sustainFocus.current ? children : <NoOverlay>{children}</NoOverlay>;
  };

  const onClose = () => {
    sustainFocus.current = false;
    setFocusedPoint(undefined);
  };

  const showLinkPopover = focusedPoint?.linkId && !!triggerRect;
  const showPointPopover =
    !!focusedPoint?.point && !!triggerRect && !focusedPoint?.linkId;

  const isScadaGraphic = graphics?.device === null;
  const scadaEditPointParams = isScadaGraphic
    ? {
        companyId,
        graphicsId: graphics.id!,
        graphic: attachment?.name,
        pointName: focusedPoint?.point?.name || "",
        deviceId: focusedPoint?.point?.externalId || "",
        id: focusedPoint?.point?.graphicNodeId,
      }
    : undefined;

  // NOTE: isGraphicsLoading boolean also checks that attachment and graphics
  // properties are not null or undefined hence using ! operator
  return (
    <Fragment key={JSON.stringify(attachment)}>
      <ProcessPictureRenderer />
      {(showLinkPopover || showPointPopover) && triggerRect && (
        <Wrapper>
          <div
            ref={anchorRef}
            style={{
              position: "fixed",
              left: triggerRect.x,
              top: triggerRect.y,
              width: triggerRect.width,
              height: triggerRect.height,
              pointerEvents: "none",
            }}
            aria-hidden
          />
          {showLinkPopover && (
            <EditScadaLinkPopover
              triggerRef={anchorRef}
              linkId={focusedPoint.linkId!}
              editParams={{
                companyId,
                graphicsId: graphics!.id!,
                graphic: attachment?.name,
                link: focusedPoint.linkId!,
              }}
              onClose={onClose}
            />
          )}
          {showPointPopover && focusedPoint.point && (
            <>
              {!focusedPoint.point.id ? (
                <CustomPopover
                  triggerRef={anchorRef}
                  isOpen={true}
                  isNonModal
                  onOpenChange={onClose}
                  placement="bottom"
                  content={
                    <PopoverHeader
                      editMode={focusedPoint.eventType === "x-click"}
                      name={focusedPoint.point.name || ""}
                      clipBoardContent={focusedPoint.point.name || ""}
                      onClose={onClose}
                      scadaEditPointParams={scadaEditPointParams}
                    />
                  }
                />
              ) : (
                <SetValuePopover
                  point={focusedPoint.point as Point & ParsedPointId} // Adding type assertion here as we know here full point exist
                  onClose={onClose}
                  triggerRef={anchorRef}
                  editMode={sustainFocus.current ? "edit" : "tooltip"}
                />
              )}
            </>
          )}
        </Wrapper>
      )}
    </Fragment>
  );
}
